import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import Badge from "@/components/base/Badge";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { formatDate } from "@/utils/format";
import { useSuperAdmin } from "../data/SuperAdminProvider";
import {
  COMPTE_STATUT_LABELS,
  COMPTE_TYPE_CONFIG,
  getFormule,
  type Compte,
} from "../data/types";
import ConfirmationModal from "./ConfirmationModal";

function initials(nom: string): string {
  return nom
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
}

/** Section de traitement des nouvelles inscriptions en attente de validation. */
export default function InscriptionsEnAttente() {
  const { comptes, chargement, erreur, recharger, changeStatut } = useSuperAdmin();
  const [pendingRefus, setPendingRefus] = useState<Compte | null>(null);

  const enAttente = useMemo(
    () =>
      comptes
        .filter((compte) => compte.statut === "en_attente")
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [comptes]
  );

  const stats = useMemo(
    () => ({
      total: enAttente.length,
      proprietaires: enAttente.filter((compte) => compte.type === "proprietaire").length,
      agences: enAttente.filter((compte) => compte.type === "agence").length,
    }),
    [enAttente]
  );

  const valider = (compte: Compte) => {
    changeStatut(compte.id, "actif", "activation");
  };

  const confirmerRefus = () => {
    if (!pendingRefus) return;
    changeStatut(pendingRefus.id, "refuse", "refus");
    setPendingRefus(null);
  };

  const columns: DataTableColumn<Compte>[] = [
    {
      key: "compte",
      header: "Compte",
      className: "min-w-[220px]",
      render: (compte) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 font-heading text-xs font-semibold text-accent-800">
            {initials(compte.nom) || "?"}
          </span>
          <span className="truncate font-label font-medium text-foreground-950">
            {compte.nom || "Nom non renseigné"}
          </span>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (compte) => (
        <Badge tone={COMPTE_TYPE_CONFIG[compte.type].type === "agence" ? "secondary" : "primary"}>
          {COMPTE_TYPE_CONFIG[compte.type].singular}
        </Badge>
      ),
    },
    {
      key: "email",
      header: "Email",
      className: "min-w-[210px]",
      render: (compte) => <span className="text-foreground-700">{compte.email || "—"}</span>,
    },
    {
      key: "telephone",
      header: "Téléphone",
      className: "min-w-[150px]",
      render: (compte) => <span className="text-foreground-700">{compte.telephone || "Non renseigné"}</span>,
    },
    {
      key: "formule",
      header: "Formule",
      className: "min-w-[130px]",
      render: (compte) => {
        const formule = getFormule(compte.formuleId);
        return <span className="text-foreground-700">{formule ? formule.nom : "Non définie"}</span>;
      },
    },
    {
      key: "createdAt",
      header: "Inscrit le",
      className: "min-w-[130px]",
      render: (compte) => <span className="text-foreground-700">{formatDate(compte.createdAt)}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: () => <Badge tone="accent" icon="ri-time-line">{COMPTE_STATUT_LABELS.en_attente}</Badge>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[300px]",
      render: (compte) => {
        const config = COMPTE_TYPE_CONFIG[compte.type];
        return (
          <div className="flex items-center justify-end gap-2">
            <Link
              to={`${config.basePath}/${compte.id}`}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-eye-line text-base" aria-hidden="true" />
              Consulter
            </Link>
            <button
              type="button"
              onClick={() => valider(compte)}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
              Valider
            </button>
            <button
              type="button"
              onClick={() => setPendingRefus(compte)}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
            >
              <i className="ri-close-circle-line text-base" aria-hidden="true" />
              Refuser
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-2xl">
        <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
          Inscriptions en attente
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
          Validez ou refusez les nouvelles demandes de compte. Un compte en attente ne peut pas accéder à son espace
          métier avant validation par un Super Admin.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon="ri-user-received-2-line" label="Demandes en attente" value={String(stats.total)} tone="accent" />
        <StatCard icon="ri-user-star-line" label="Propriétaires" value={String(stats.proprietaires)} tone="primary" />
        <StatCard icon="ri-building-2-line" label="Agences" value={String(stats.agences)} tone="secondary" />
      </div>

      {chargement && enAttente.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!chargement && erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void recharger()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!chargement && !erreur && enAttente.length === 0 && (
        <EmptyState
          icon="ri-user-received-2-line"
          title="Aucune inscription en attente"
          description="Toutes les demandes de compte ont été traitées. Les nouvelles inscriptions apparaîtront ici automatiquement."
        />
      )}

      {enAttente.length > 0 && (
        <DataTable
          columns={columns}
          rows={enAttente}
          rowKey={(compte) => compte.id}
          minWidthClass="min-w-[1320px]"
          caption="Inscriptions en attente"
        />
      )}

      <ConfirmationModal
        open={Boolean(pendingRefus)}
        onClose={() => setPendingRefus(null)}
        onConfirm={confirmerRefus}
        title="Refuser l'inscription"
        description={
          pendingRefus
            ? `Refuser l'inscription de « ${pendingRefus.nom || pendingRefus.email} » ? Le compte ne pourra pas accéder à son espace métier. Les informations sont conservées à titre d'historique. Cette action est tracée dans le journal d'audit.`
            : ""
        }
        confirmLabel="Refuser l'inscription"
        icon="ri-close-circle-line"
        tone="danger"
      />
    </div>
  );
}